import ini from 'ini';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import PlatformUtils,
{
    BOARD_FILES,
    TIBBO_DATATYPE_BOUNDS,
    TIBBO_DEVICE_ID,
    RUNTIMES,
    RESERVED_KEYWORDS,
} from '@appblocks/platforms/PlatformUtils';
import NodeLink from '@appblocks/codegeneration/Nodes/NodeLink';
import {
    Device,
    FeatureType,
    SettingType,
    Setting,
    ValidationMessage,
    ValidationMessageType,
    TibboDataType,
    FlowLink,
    CodeBlock,
    BlockType,
    SettingOption,
    DeviceType,
    IOLinesMode,
    AppBlockPort,
} from '@appblocks/codegeneration/types';
import Tibbits from '@appblocks/codegeneration/Tibbits';
import * as ProjectUtils from './ProjectUtils';


const SCHEMA_VERSION = '0.1.19';

export default class ProjectConfiguration {
    id: string;

    name: string;

    device: Device;

    description: string;

    shortDescription: string;

    features: Array<BaseFeature> = [];

    project_id: string;

    version: string;

    static PLATFORMS_VERSION = '0_00';

    parentCache: { [key: string]: BaseFeature } = {};

    updatedAt: Date;

    runtime: string = RUNTIMES.TIOS;

    constructor() {
        this.version = SCHEMA_VERSION;
    }

    static fromJson(inputJson: any): ProjectConfiguration {
        const obj = JSON.parse(JSON.stringify(inputJson));
        const project = new ProjectConfiguration();
        if (obj.id) {
            project.id = obj.id.toString();
        }
        if (!obj.version) {
            obj.version = ProjectUtils.migrations[0].from;
        }
        ProjectUtils.migrate(obj, SCHEMA_VERSION);
        project.name = obj.name;
        project.device = obj.device;
        project.project_id = obj.id;
        project.version = obj.version;
        project.updatedAt = new Date();
        project.runtime = obj.runtime;
        if (!project.runtime) {
            project.runtime = RUNTIMES.TIOS;
        }

        if (typeof (obj.device) === 'string') {
            const tmp = PlatformUtils.getDevice(obj.device);
            if (tmp) {
                project.device = tmp;
            }
        } else {
            const tmp = PlatformUtils.getDevice(obj.device.id);
            if (tmp) {
                project.device = tmp;
            }
        }
        project.description = obj.description;
        project.shortDescription = obj.shortDescription;
        for (let i = 0; i < obj.features.length; i += 1) {
            const feature = FeatureUtils.fromJson(obj.features[i], project);
            if (feature !== undefined) {
                project.features.push(feature);
            }
        }
        project.initFeatures();
        project.validate();
        return project;
    }

    parseCodeExpression(argExpression: string,
        sourceParams: any[] = [], dataType: TibboDataType = TibboDataType.STRING): string {
        const global_vars = this.getFeature('global_vars');
        let projectVariables: BaseFeature[] = [];
        if (global_vars) {
            projectVariables = this.getVariables();
        }
        let strExpression = '';
        let currentIndex = 0;
        let templateStart = 0;
        let templateEnd = 0;

        while (currentIndex < argExpression.length) {
            templateStart = argExpression.indexOf('{{', currentIndex);
            templateEnd = argExpression.indexOf('}}', currentIndex);
            let subExpression = '';
            if (templateStart === -1 || templateEnd === -1 || templateStart > templateEnd) {
                // no expression found
                if (dataType === TibboDataType.STRING) {
                    let subStr = argExpression.substring(currentIndex,
                        argExpression.length);
                    subStr = FeatureUtils.escapeString(subStr, this.runtime);
                    if (strExpression.length !== 0) {
                        strExpression += ' + ';
                    }
                    strExpression += `"${subStr}"`;
                } else {
                    strExpression
                        += argExpression.substring(currentIndex, argExpression.length);
                }
                break;
            }
            if (currentIndex !== templateStart) {
                if (dataType === TibboDataType.STRING) {
                    let subStr = argExpression.substring(currentIndex, templateStart);
                    subStr = FeatureUtils.escapeString(subStr, this.runtime);
                    if (currentIndex !== 0) {
                        strExpression += '+';
                    }
                    strExpression += `"${subStr}" + `;
                } else {
                    strExpression += argExpression.substring(currentIndex, templateStart);
                }
                currentIndex = templateStart;
                continue;
            } else {
                templateStart += 2; // skip {{
                subExpression = argExpression.substring(templateStart, templateEnd);
                currentIndex = templateEnd + 2;
            }

            // setting value
            let regex = /\$\$([A-Za-z0-9_]+)/gm;
            let m = regex.exec(subExpression);
            while (m !== null) {
                if (m.index === regex.lastIndex) {
                    regex.lastIndex += 1;
                }
                const match = m[1];
                subExpression = subExpression.replace(`$$${match}`, `stg_get("${match}",0)`);

                m = regex.exec(subExpression);
            }

            // variable value
            regex = /\$([A-Za-z0-9_]+)/gm;
            m = regex.exec(subExpression);
            while (m !== null) {
                if (m.index === regex.lastIndex) {
                    regex.lastIndex += 1;
                }

                const match = m[1];
                // get variable
                let variableName;
                let variableFeature;
                let variableDataType;
                for (let j = 0; j < projectVariables.length; j++) {
                    if (projectVariables[j].getSettingValue('VARNAME') === match
                        || projectVariables[j].getID() === match) {
                        variableFeature = projectVariables[j];
                        variableName = projectVariables[j].getSettingValue('VARNAME');
                        variableDataType = projectVariables[j].getSettingValue('DATATYPE');
                        break;
                    }
                }
                for (let j = 0; j < sourceParams.length; j++) {
                    if (sourceParams[j].name === match) {
                        variableName = sourceParams[j].name;
                        variableDataType = sourceParams[j].dataType;
                        break;
                    }
                }

                if (variableName !== undefined) {
                    let value = `${this.convertDataType(
                        match,
                        variableDataType,
                        dataType,
                    )}`;
                    if (variableFeature !== undefined) {
                        value = `${this.convertDataType(
                            variableName,
                            variableDataType,
                            dataType,
                        )}`;
                    }
                    subExpression = subExpression.replace(`$${match}`, value);
                }

                m = regex.exec(subExpression);
            }
            strExpression += subExpression;
            if (dataType === TibboDataType.STRING) {
                if (argExpression.indexOf('{{', currentIndex) === currentIndex) {
                    strExpression += '+';
                }
            }
        }
        // handle empty string
        if (strExpression === '' && dataType === TibboDataType.STRING) {
            switch (this.runtime) {
                case RUNTIMES.MICROPYTHON:
                    strExpression = '\'\'';
                    break;
                default:
                    strExpression = '""';
                    break;
            }
        }
        return strExpression;
    }

    initFeatures() {
        const requiredFeatures = ['boardsetting', 'general', 'global_vars'];
        const capabilities = PlatformUtils.getCapabilities(this);
        if (capabilities.includes('ethernet')) {
            requiredFeatures.push('net');
        }
        requiredFeatures.forEach((reqFeature) => {
            let found = false;
            for (let i = 0; i < this.features.length; i += 1) {
                if (this.features[i].id === reqFeature) {
                    found = true;
                    this.features[i].initSettings(this);
                    break;
                }
            }
            if (!found) {
                const feature = FeatureUtils.getCodeItem(reqFeature, this);
                if (feature && FeatureUtils.isSupported(feature, this)) {
                    feature.initSettings(this);
                    this.addFeature(feature);
                }
            }
        });
        for (let i = 0; i < this.features.length; i += 1) {
            const feature = this.features[i];
            if (!FeatureUtils.isSupported(feature, this)) {
                this.features.splice(i, 1);
                i--;
            }
        }
    }

    setProject(features: Array<BaseFeature>, project: ProjectConfiguration): void {
        const projectFeatures = features;
        for (let i = 0; i < projectFeatures.length; i += 1) {
            projectFeatures[i].project = project;
            for (let j = 0; j < projectFeatures[i].settings.length; j += 1) {
                const setting = projectFeatures[i].settings[j];
                if (setting.refId !== undefined) {
                    if (setting.settingType === SettingType.ARRAY) {
                        if (setting.value === undefined) {
                            setting.value = [];
                        }
                        this.setProject(setting.value, project);
                    }
                }
            }
        }
    }

    toJson(): JSON {
        let deviceId: any = this.device;
        if (this.device.id) {
            deviceId = this.device.id;
        }
        const result: any = {
            id: this.id,
            name: this.name,
            device: deviceId,
            description: this.description,
            shortDescription: this.shortDescription,
            version: this.version,
            runtime: this.runtime,
            features: [],
        };
        for (let i = 0; i < this.features.length; i += 1) {
            result.features.push(FeatureUtils.toJson(this.features[i]));
        }
        return result;
    }

    toString(): string {
        return JSON.stringify(this.toJson());
    }

    exportINI(): string {
        try {
            const out = ini.parse('');
            out.desc_short = this.name;
            out.description = this.description;
            out.base = {};
            out.base.board = `boards/${BOARD_FILES[this.device.id]}.brd`;
            out.base.device = this.device.id;
            out.sockets = {};
            out.bom = {};


            for (let i = 0; i < this.features.length; i += 1) {
                const feature = this.features[i];
                if (feature.featureType === FeatureType.TIBBIT) {
                    out.sockets[`s${feature.getSettingValue('SOCKET')}`] = feature.getSettingValue('TIBBIT');
                    if (out.bom[`tibbit_${feature.getSettingValue('TIBBIT')}`] === undefined) {
                        out.bom[`tibbit_${feature.getSettingValue('TIBBIT')}`] = 1;
                    } else {
                        out.bom[`tibbit_${feature.getSettingValue('TIBBIT')}`] += 1;
                    }
                }
                if (feature.id === 'wireless_module') {
                    if (feature.getSettingValue('MODULE') !== '') {
                        out.sockets.sga = feature.getSettingValue('MODULE').toLowerCase();
                        out.bom[`tibbit_${feature.getSettingValue('MODULE').toLowerCase()}`] = 1;
                    }
                }
            }
            out.bom[`board_${BOARD_FILES[this.device.id]}`] = 1;
            // out.messages = [];
            // out.messages['ERR[]'] = '';
            out.options = {};
            out.options.b_pparallel = '0';
            out.options.p5v_direct = this.getSettingValue('boardsetting', '5VDIRECT') === '1';
            out.options.wifi_antenna = this.getSettingValue('boardsetting', 'WIFIANTENNA') === '1';
            out.options.gprs_antenna = this.getSettingValue('boardsetting', 'GPRSANTENNA') === '1';
            out.options.poe_jumper = this.getSettingValue('boardsetting', 'POE');
            out.options.tpb = 'true';
            out.options.vpk = 'false';
            out.options.din = 'false';
            out.options.asm_rp = 'asm+rp';
            if (this.getSettingValue('lcd', 'ENABLED') === '1') {
                out.options.tpb = 'tpb2lg2';
                delete out.bom[`board_${BOARD_FILES[this.device.id]}`];
                out.bom.board_tpb2lg2 = 1;
            }

            let iniOut = '';
            Object.keys(out).forEach((sectionKey) => {
                const section = out[sectionKey];
                if (typeof (section) === 'string') {
                    iniOut += `${sectionKey} = "${out[sectionKey]}"\n`;
                    return;
                }
                iniOut += `\n[${sectionKey}]\n`;
                Object.keys(section).forEach((key) => {
                    switch (sectionKey) {
                        case 'bom':
                            iniOut += `${key} = ${section[key]} \n`;
                            break;
                        default:
                            if (section[key].toString() === 'false' || section[key].toString() === 'true') {
                                iniOut += `${key} = ${section[key]}\n`;
                            } else {
                                iniOut += `${key} = "${section[key]}"\n`;
                            }
                            break;
                    }
                });
            });

            // iniOut = ini.encode(out, {
            //     section: undefined,
            //     whitespace: true
            // });
            return iniOut;
        } catch (ex) {
            return '';
        }
    }

    addFeature(feature: BaseFeature): void {
        let found = false;
        if (feature.id.indexOf('node_') < 0) {
            for (let i = 0; i < this.features.length; i++) {
                if (this.features[i].id === feature.id) {
                    if (this.features[i].singleInstance) {
                        found = true;
                    }
                }
            }
        }
        if (!found) {
            const nameSettings = [];
            for (let i = 0; i < feature.settings.length; i++) {
                const setting = feature.settings[i];
                if (setting.settingType === SettingType.NAME) {
                    nameSettings.push(setting);
                }
            }
            if (nameSettings.length > 0) {
                const variables = this.getVariables();
                const variablesMap = new Map();
                for (let i = 0; i < variables.length; i++) {
                    const variable = variables[i];
                    const variableName = variable.getSettingValue('VARNAME');
                    if (variableName) {
                        const match = variableName.match(/^([a-zA-Z_]+)(\d*)$/);
                        if (match) {
                            const baseString = match[1];
                            const number = match[2] ? parseInt(match[2], 10) : 0;
                            if (variablesMap.has(baseString)) {
                                const currentNumber = variablesMap.get(baseString);
                                if (number > currentNumber) {
                                    variablesMap.set(baseString, number);
                                }
                            } else {
                                variablesMap.set(baseString, number);
                            }
                        }
                    }
                }
                for (let i = 0; i < nameSettings.length; i++) {
                    const setting = nameSettings[i];
                    const featureSettingValue = feature.getSettingValue(setting.id);
                    if (variablesMap.has(featureSettingValue)) {
                        const currentNumber = variablesMap.get(featureSettingValue);
                        const newNumber = currentNumber + 1;
                        feature.setSettingValue(setting.id, `${featureSettingValue}${newNumber}`);
                    }
                }
            }
            this.features.push(feature);
        }
    }

    removeFeature(featureId: string, features?: BaseFeature[]) {
        let currentFeatures = this.features;
        if (features !== undefined) {
            currentFeatures = features;
        }
        for (let i = 0; i < currentFeatures.length; i++) {
            const feature = currentFeatures[i];
            for (let k = 0; k < feature.settings.length; k++) {
                const setting = feature.settings[k];
                if ((setting.settingType === SettingType.ARRAY)
                    && setting.value !== undefined) {
                    for (let j = 0; j < setting.value.length; j++) {
                        const settingId = setting.value[j].getID();
                        if (settingId === featureId && features !== undefined) {
                            this.removeFeature(settingId, setting.value);
                            break;
                        }
                    }
                }
            }
            if (feature.getID() === featureId) {
                currentFeatures.splice(i, 1);
                break;
            }
            for (let i = 0; i < feature.settings.length; i++) {
                const setting = feature.settings[i];
                if ((setting.settingType === SettingType.ARRAY)
                    && setting.value !== undefined) {
                    this.removeFeature(featureId, setting.value);
                }
            }
        }
    }

    checkLinkedVariable(feature: BaseFeature): ValidationMessage[] {
        let messages: ValidationMessage[] = [];
        const varFeature = this.getFeature('global_vars');
        for (let i = 0; i < feature.settings.length; i++) {
            const setting = feature.settings[i];
            if (setting.dataType !== undefined
                && setting.dataType !== TibboDataType.SETTING
                && setting.settingType === SettingType.NAME) {
                const newVariable = this.createVariable(feature, setting.id);
                if (newVariable !== undefined) {
                    messages.push({
                        message: `Variable ${newVariable.getSettingValue('VARNAME')} created`,
                        messageType: ValidationMessageType.ADD,
                        featureId: varFeature?.getID(),
                        settingId: 'AUTOVARS',
                        data: FeatureUtils.toJson(newVariable),
                    });
                }
            }
            if ((setting.settingType === SettingType.ARRAY)
                && setting.value !== undefined) {
                for (let j = 0; j < setting.value.length; j++) {
                    messages = messages.concat(this.checkLinkedVariable(setting.value[j]));
                }
            }
        }
        return messages;
    }

    checkParamsValid(node: BaseNode, errors: ValidationMessage[]): void {
        const vars = this.getFeature('global_vars')?.getSettingValue('AUTOVARS')
            .concat(this.getFeature('global_vars')?.getSettingValue('VARS'));
        const inputs = node.getInputs();
        for (let i = 0; i < inputs.length; i++) {
            const params = inputs[i].params;
            if (params.length > 0) {
                for (let j = 0; j < params.length; j++) {
                    const param = params[j];
                    const paramValue = this.getNodeParameterValue(node, param.name);
                    const arg = node.getSettingValue('ARGUMENTS').filter((a: BaseFeature) => a.getSettingValue('TARGETPARAM') === param.name)[0];
                    if (paramValue === undefined) {
                        errors.push({
                            message: `Value for ${param.name} is invalid`,
                            messageType: ValidationMessageType.ERROR,
                            featureId: node.getID(),
                            settingId: param.name,
                        });
                        continue;
                    }
                    if (/^{{\$.*}}$/.test(paramValue)) {
                        continue;
                    }
                    // check for auto var
                    if (param.dataType === TibboDataType.CUSTOM) {
                        const linkedVariableId = node.getSettingValue('VARIABLE');
                        for (let k = 0; k < vars.length; k++) {
                            const autovar = vars[k];
                            if (autovar.getID() === linkedVariableId) {
                                const varname = autovar.getSettingValue('VARNAME');
                                let lowerBound = autovar.getSettingValue('MINIMUM');
                                let upperBound = autovar.getSettingValue('MAXIMUM');
                                let message = '';
                                if (upperBound !== '' && lowerBound !== '') {
                                    lowerBound = Number(lowerBound);
                                    upperBound = Number(upperBound);
                                    if ((Number(paramValue) < lowerBound
                                        || Number(paramValue) > upperBound)
                                        && paramValue.match(/\d/)) {
                                        message = `${varname} must be between ${lowerBound} and ${upperBound}`;
                                    }
                                } else if (lowerBound !== '') {
                                    if (Number(paramValue) < lowerBound
                                        && paramValue.match(/\d/)) {
                                        message = `${varname} must be greater than ${lowerBound}`;
                                    }
                                } else if (upperBound !== '') {
                                    if (Number(paramValue) > upperBound
                                        && paramValue.match(/\d/)) {
                                        message = `${varname} must be less than ${upperBound}`;
                                    }
                                }
                                if (message !== '') {
                                    errors.push({
                                        message,
                                        messageType: ValidationMessageType.ERROR,
                                        featureId: node.getID(),
                                        settingId: param.name,
                                    });
                                }
                            }
                        }
                    }
                    // check for numerical values
                    if (TIBBO_DATATYPE_BOUNDS[param.dataType]) {
                        if (paramValue.trim() === '') {
                            errors.push({
                                message: `Value must be set for ${param.name}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: arg.getID(),
                                settingId: param.name,
                            });
                            continue;
                        }
                        const lowerBound = param.min ? param.min
                            : TIBBO_DATATYPE_BOUNDS[param.dataType].min;
                        const upperBound = param.max ? param.max
                            : TIBBO_DATATYPE_BOUNDS[param.dataType].max;
                        if ((lowerBound !== undefined && Number(paramValue) < lowerBound)
                            || (upperBound !== undefined && Number(paramValue) > upperBound)
                            || !paramValue.match(/\d/)) {
                            errors.push({
                                message: `${param.name} must be between ${lowerBound} and ${upperBound}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: node.getID(),
                                settingId: param.name,
                            });
                            continue;
                        }
                    }
                    if (param.dataType === TibboDataType.STRING) {
                        if (param.min && paramValue.length < param.min) {
                            errors.push({
                                message: `Value for ${param.name} must be at least ${param.min}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: node.getID(),
                                settingId: param.name,
                            });
                        }
                        if (param.max && paramValue.length > param.max) {
                            errors.push({
                                message: `Value for ${param.name} must be less than ${param.max}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: node.getID(),
                                settingId: param.name,
                            });
                        }
                        if (param.regex && !param.regex.test(paramValue)) {
                            errors.push({
                                message: `Value for ${param.name} must match authorized pattern`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: node.getID(),
                                settingId: param.name,
                            });
                        }
                    }
                }
            }
        }
    }

    checkSettingsValid(feature: BaseFeature, errors: ValidationMessage[]): void {
        if (!feature.getEnabled()) return;
        let parent = this.getParent(feature);
        if (parent) {
            let rootParent = this.getParent(parent);
            while (rootParent) {
                parent = rootParent;
                rootParent = this.getParent(parent);
            }
            if (!parent.getEnabled()) {
                return;
            }
        }
        const matchVariable = /\{\{\$[A-Za-z0-9]+\}\}/gi;
        const matchHex = /&h[A-Fa-f0-9]+/gi;
        const matchBin = /&b[01]/gi;
        const matchHexLiteral = /0x[A-Fa-f0-9]+/gi;
        const matchStartsWithOperator = /^[+\-*/%]/gi;
        const matchEndsWithOperator = /[+\-*/%]$/gi;
        for (let i = 0; i < feature.settings.length; i++) {
            const setting = feature.settings[i];
            if (setting.value === undefined) {
                errors.push({
                    message: `Value for ${setting.name} is invalid`,
                    messageType: ValidationMessageType.UPDATE,
                    featureId: feature.getID(),
                    settingId: setting.id,
                    data: setting.default,
                });
            }
            if (setting.required && 'refId' in setting) {
                if (!setting.value && setting.required) {
                    errors.push({
                        message: `${setting.name} is not defined`,
                        messageType: ValidationMessageType.ERROR,
                        settingId: setting.id,
                        featureId: feature.getID(),
                    });
                }
                if (setting.refId === 'luis_group' || setting.refId === 'ds_group') {
                    const groups: Array<BaseFeature> = setting.value;
                    const settingsHashmap: Map<string, string[]> = new Map();
                    for (let j = 0; j < groups.length; j++) {
                        const SETTINGS: Array<BaseFeature> = groups[j].getSettingValue('SETTINGS');
                        for (let k = 0; k < SETTINGS.length; k++) {
                            const setting = SETTINGS[k];
                            const linkedSetting: string = setting.getSettingValue('SETTINGID');
                            if (settingsHashmap.has(linkedSetting)) {
                                const value: string[] | undefined = settingsHashmap
                                    .get(linkedSetting);
                                if (!value) {
                                    continue;
                                }
                                value.push(setting.getSettingValue('ID'));
                                settingsHashmap.set(linkedSetting, value);
                                continue;
                            }
                            settingsHashmap.set(linkedSetting, [setting.getSettingValue('ID')]);
                        }
                    }
                    const hashmapKeys: IterableIterator<string> = settingsHashmap.keys();
                    for (let j = 0; j < settingsHashmap.size; j++) {
                        const key = hashmapKeys.next().value;
                        const values = settingsHashmap.get(key);
                        if (values && values.length > 1) {
                            let linkedSetting = 'Same setting';
                            const stgSettings: Array<BaseFeature> = this.getSetting('stg', 'SETTINGS')?.value;
                            for (let k = 0; k < stgSettings.length; k++) {
                                if (stgSettings[k].getSettingValue('ID') === key) {
                                    linkedSetting = stgSettings[k].getSettingValue('SETTINGID');
                                    break;
                                }
                            }
                            for (let k = 0; k < values.length; k++) {
                                errors.push({
                                    message: `${linkedSetting} cannot be exposed more than once in ${feature.name}`,
                                    messageType: ValidationMessageType.ERROR,
                                    featureId: values[k],
                                    settingId: setting.id,
                                });
                            }
                        }
                    }
                }
            }
            if ([
                SettingType.NAME,
                SettingType.STRINGVARIABLE,
                SettingType.STRING,
            ].includes(setting.settingType)) {
                if (setting.min !== undefined && setting.value.length < setting.min) {
                    errors.push({
                        message: `${setting.name} must be at least ${setting.min} character(s)`,
                        messageType: ValidationMessageType.ERROR,
                        featureId: feature.getID(),
                        settingId: setting.id,
                    });
                }
                if (setting.max && setting.value.length > setting.max) {
                    errors.push({
                        message: `Value for ${setting.name} must be less than ${setting.max}`,
                        messageType: ValidationMessageType.ERROR,
                        settingId: setting.id,
                        featureId: feature.getID(),
                    });
                }
            }
            switch (setting.settingType) {
                case SettingType.NAME:
                    {
                        const rIsLetter = /^[a-zA-Z]+$/;
                        if (setting.value.trim() === '') {
                            errors.push({
                                message: `Name not defined for ${setting.name}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: feature.getID(),
                                settingId: setting.id,
                            });
                        }
                        if (!rIsLetter.test(setting.value[0])) {
                            errors.push({
                                message: 'Name must start with letter',
                                messageType: ValidationMessageType.ERROR,
                                featureId: feature.getID(),
                                settingId: setting.id,
                            });
                        }
                    }
                    break;
                case SettingType.IP:
                    {
                        const exp = /^(?=.*[^.]$)((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.?){4}$/;
                        if (!exp.test(setting.value)) {
                            errors.push({
                                message: `Invalid IP Address for ${setting.name}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: feature.getID(),
                                settingId: setting.id,
                            });
                        }
                    }
                    break;
                case SettingType.OPTION:
                    {
                        const options = feature.getSettingOptions(setting.id);
                        if (options === undefined) {
                            continue;
                        }
                        let found = false;
                        for (let k = 0; k < options.length; k += 1) {
                            if (options[k].value === setting.value) {
                                found = true;
                                break;
                            }
                        }
                        if (!found && options.length > 0) {
                            errors.push({
                                message: `${setting.name} is not valid`,
                                messageType: ValidationMessageType.UPDATE,
                                settingId: setting.id,
                                featureId: feature.getID(),
                                data: options[0].value,
                            });
                        }
                        if (!found && options.length === 0 && setting.value !== setting.default) {
                            errors.push({
                                message: `${setting.name} is not valid`,
                                messageType: ValidationMessageType.UPDATE,
                                settingId: setting.id,
                                featureId: feature.getID(),
                                data: setting.default,
                            });
                        }
                    }
                    break;
                case SettingType.NUMBER:
                    if (setting.max !== undefined) {
                        if (setting.value > setting.max) {
                            errors.push({
                                message: `Value for ${setting.name} must be less than ${setting.max}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: feature.getID(),
                                settingId: setting.id,
                            });
                        }
                    }
                    if (setting.min !== undefined) {
                        if (setting.value < setting.min) {
                            errors.push({
                                message: `Value for ${setting.name} must be at least ${(setting.min).toString()}`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: feature.getID(),
                                settingId: setting.id,
                            });
                        }
                    }
                    break;
                case SettingType.ARRAY:
                    for (let j = 0; j < setting.value.length; j++) {
                        this.checkSettingsValid(setting.value[j], errors);
                    }
                    break;
                case SettingType.STRINGVARIABLE:
                    {
                        if (setting.dataType === undefined
                            || setting.dataType === TibboDataType.STRING) {
                            break;
                        }
                        const regex = /\{\{\$(\w+)\}\}/g;
                        const m = regex.exec(setting.value);
                        if (m !== null) {
                            break;
                        }
                        if (/[A-Za-z]+/i.test(setting.value
                            .replaceAll(matchVariable, '')
                            .replaceAll(matchHex, '')
                            .replaceAll(matchBin, '')
                            .replaceAll(matchHexLiteral, ''))
                            || matchStartsWithOperator.test(setting.value.trim())
                            || matchEndsWithOperator.test(setting.value.trim())) {
                            errors.push({
                                message: `Value for ${setting.name} is invalid`,
                                messageType: ValidationMessageType.ERROR,
                                featureId: feature.getID(),
                                settingId: setting.id,
                            });
                        }
                        const min = setting.min ? setting.min
                            : TIBBO_DATATYPE_BOUNDS[setting.dataType].min;
                        const max = setting.max ? setting.max
                            : TIBBO_DATATYPE_BOUNDS[setting.dataType].max;
                        if (/[0-9]+/i.test(setting.value)) {
                            if ((min !== undefined
                                && setting.value < min)
                                || (max !== undefined
                                    && setting.value > max)) {
                                errors.push({
                                    message: `Value for ${setting.name} must be between ${TIBBO_DATATYPE_BOUNDS[setting.dataType].min} and ${TIBBO_DATATYPE_BOUNDS[setting.dataType].max}`,
                                    messageType: ValidationMessageType.ERROR,
                                    featureId: feature.getID(),
                                    settingId: setting.id,
                                });
                            }
                        }
                    }
                    break;
                default:

                    break;
            }
        }
    }

    validateFeature(feature: BaseFeature): ValidationMessage[] {
        let errors = feature.validate();
        for (let i = 0; i < feature.settings.length; i++) {
            const setting = feature.settings[i];
            if (setting.settingType === SettingType.ARRAY) {
                for (let j = 0; j < setting.value.length; j++) {
                    errors = errors.concat(this.validateFeature(setting.value[j]));
                }
            }
        }
        return errors;
    }

    validateLoop(): Array<ValidationMessage> {
        let errors: Array<ValidationMessage> = [];
        const { features } = this;
        const names: { [key: string]: any } = {};

        for (let i = 0; i < features.length; i += 1) {
            const feature = features[i];
            const featureId = feature.getID();
            let featureErrors: Array<ValidationMessage> = [];
            let enabled = true;
            if (!feature.getEnabled()) {
                enabled = false;
            }
            if (enabled) {
                featureErrors = this.validateFeature(feature);
            }
            this.getSettingsWithName(feature, names);
            this.checkSettingsValid(feature, featureErrors);
            if (feature.id.indexOf('node_') === 0) {
                if (feature.id !== 'node_link') {
                    this.checkParamsValid(feature as BaseNode, featureErrors);
                }
            }
            errors = errors.concat(this.checkLinkedVariable(feature));
            if (featureErrors.length > 0) {
                for (let j = 0; j < featureErrors.length; j += 1) {
                    if (featureErrors[j].featureId === '') {
                        featureErrors[j].message = `${features[i].name}: ${featureErrors[j].message}`;
                    }
                }
            }
            errors = errors.concat(featureErrors);
        }

        Object.keys(names).forEach((key) => {
            let length = names[key].length;
            for (let i = 0; i < names[key].length; i++) {
                const feature = this.getFeature(names[key][i].featureId);
                if (RESERVED_KEYWORDS[this.runtime].includes(key.toLowerCase())) {
                    errors.push({
                        message: `[${key}] is a reserved keyword`,
                        messageType: ValidationMessageType.ERROR,
                        featureId: names[key][i].featureId,
                        settingId: names[key][i].settingId,
                    });
                }
                if (feature?.id === 'variable' && feature.getSettingValue('FEATURE')) {
                    length--;
                }
            }
            if (length > 1) {
                for (let i = 0; i < names[key].length; i++) {
                    const feature = this.getFeature(names[key][i].featureId);
                    if (feature?.id === 'variable' && feature.getSettingValue('FEATURE')) {
                        continue;
                    }
                    errors.push({
                        message: `Name conflict: ${key}. No duplicate names allowed.${this.runtime === RUNTIMES.TIOS ? ' Tibbo Basic identifiers are case-insensitive.' : ''}`,
                        messageType: ValidationMessageType.ERROR,
                        featureId: names[key][i].featureId,
                        settingId: names[key][i].settingId,
                    });
                }
            }
        });

        const model: { [socket: string]: string } = {};
        for (let i = 0; i < features.length; i += 1) {
            if (features[i].featureType === FeatureType.TIBBIT) {
                model[features[i].getSettingValue('SOCKET')] = features[i].getSettingValue('TIBBIT');
            }
        }
        // validate flow items

        return errors;
    }

    validateVariables(): Array<ValidationMessage> {
        const messages: Array<ValidationMessage> = [];
    

        
        return messages;
    }


    /**
     *
     * Getting the variable linked to a setting of a feature
     * @param feature
     * @param settingId
     * @returns
     */
    getLinkedVariable(feature: BaseFeature, settingId: string) {
        const featureId = feature.getID();
        const varFeature = this.getFeature('global_vars');
        const variables = this.getVariables();
        for (let i = 0; i < variables.length; i += 1) {
            const variable = variables[i];
            if (variable.getSettingValue('FEATURE') === featureId
                && variable.getSettingValue('FEATURE_SETTING') === settingId) {
                return variable;
            }
        }
        return undefined;
    }

    /**
     * Adds variable linked to a setting of a feature
     * @param feature
     * @param settingId
     * @returns
     */
    createVariable(feature: BaseFeature, settingId: string): BaseFeature | undefined {
        const featureId = feature.getID();
        let found = false;
        found = this.getLinkedVariable(feature, settingId) !== undefined;
        let variable;
        if (!found) {
            variable = FeatureUtils.getCodeItem('variable', this);
            variable.initSettings(this);
            variable.setSettingValue('FEATURE', featureId);
            variable.setSettingValue('FEATURE_SETTING', settingId);
            let VARNAME = feature.getSettingValue(settingId);
            const variables = this.getVariables();
            let maxNumber = 0;
            const regex = new RegExp(`^${VARNAME}[0-9]*$`);

            for (let i = 0; i < variables.length; i++) {
                const existingName = variables[i].getSettingValue('VARNAME');
                if (existingName === VARNAME) {
                    maxNumber = Math.max(maxNumber, 1);
                } else if (regex.test(existingName)) {
                    const num = parseInt(existingName.replace(VARNAME, ''), 10);
                    if (!Number.isNaN(num)) {
                        maxNumber = Math.max(maxNumber, num + 1);
                    }
                }
            }

            if (maxNumber > 0) {
                VARNAME = `${VARNAME}${maxNumber}`;
            }
            variable.setSettingValue('VARNAME', VARNAME);
            if (feature.id === 'stg_setting') {
                variable.setSettingValue('NAME', feature.getSettingValue('NAME'));
            }
            const dataType = feature.getSetting(settingId)?.dataType;
            variable.setSettingValue('DATATYPE', dataType);
            switch (dataType) {
                case TibboDataType.FLOAT:
                    variable.setSettingValue('DEFAULT', '0');
                    break;
                case TibboDataType.BOOLEAN:
                case TibboDataType.BYTE:
                    variable.setSettingValue('DEFAULT', '1');
                    break;
                case TibboDataType.INTEGER:
                    variable.setSettingValue('DEFAULT', '0');
                    break;
                default:
                    variable.setSettingValue('DEFAULT', '');
                    break;
            }

            const bounds = this.getSetting(feature.id, settingId);
            if (bounds && dataType) {
                if (!bounds.min) {
                    bounds.min = TIBBO_DATATYPE_BOUNDS[dataType]?.min;
                }
                if (!bounds.max) {
                    bounds.max = TIBBO_DATATYPE_BOUNDS[dataType]?.max;
                }
                variable.setSettingValue('MINIMUM', bounds.min);
                variable.setSettingValue('MAXIMUM', bounds.max);
            }
        }
        return variable;
    }

    validate(): Array<ValidationMessage> {
        let messages: ValidationMessage[] = [];
        messages = messages.concat(this.validateFeatures());
        messages = messages.concat(this.validateLoop());
        messages = messages.concat(this.validateVariables());

        return messages;
    }


    getSettingValue(featureId: string, settingId: string) {
        for (let i = 0; i < this.features.length; i += 1) {
            const feature = this.features[i];
            if (feature.id === featureId) {
                return feature.getSettingValue(settingId);
            }
        }
        return undefined;
    }

    getSetting(featureId: string, settingId: string) {
        for (let i = 0; i < this.features.length; i += 1) {
            const feature = this.features[i];
            if (feature.id === featureId) {
                return feature.getSetting(settingId);
            }
        }
        return undefined;
    }

    setSettingValue(featureId: string, settingId: string, settingValue: any) {
        for (let i = 0; i < this.features.length; i += 1) {
            const feature = this.features[i];
            if (feature.id === featureId) {
                feature.setSettingValue(settingId, settingValue);
            }
        }
    }

    validateFeatures(): ValidationMessage[] {
        const messages: ValidationMessage[] = [];
        for (let i = 0; i < this.features.length; i += 1) {
            const feature: BaseFeature = this.features[i];
            feature.initSettings(this);
        }
        for (let i = 0; i < this.features.length; i += 1) {
            const feature: BaseFeature = this.features[i];
            const dependencies = feature.getDependencies();
            if (feature.linkedFeature && feature.linkedFeature !== 'general') {
                dependencies.push(feature.linkedFeature);
            }
            if (feature.getEnabled()) {
                // Check settings requirements
                for (let j = 0; j < dependencies.length; j += 1) {
                    let found = false;
                    for (let k = 0; k < this.features.length; k += 1) {
                        if (dependencies[j] === this.features[k].id) {
                            found = true;
                            if (this.features[k].getSettingValue('ENABLED') === '0') {
                                messages.push({
                                    featureId: this.features[k].getID(),
                                    settingId: 'ENABLED',
                                    message: `${this.features[k].name} is required by ${feature.name}`,
                                    messageType: ValidationMessageType.UPDATE,
                                    data: '1',
                                });
                            }
                            break;
                        }
                    }
                    if (!found) {
                        const dependedFeature = FeatureUtils.getCodeItem(dependencies[j], this);
                        if (dependedFeature) {
                            dependedFeature.initSettings(this);
                            dependedFeature.setSettingValue('ENABLED', '1');

                            messages.push({
                                featureId: undefined,
                                settingId: undefined,
                                message: `${dependedFeature.name} is required by ${feature.name}`,
                                messageType: ValidationMessageType.ADD,
                                data: FeatureUtils.toJson(dependedFeature),
                            });
                        } else {
                            messages.push({
                                featureId: undefined,
                                settingId: undefined,
                                message: `${dependencies[j]} is required by ${feature.name} but not found`,
                                messageType: ValidationMessageType.ERROR,
                            });
                        }
                    }
                }
            } else {
                for (let j = 0; j < dependencies.length; j += 1) {
                    const dependency = dependencies[j];
                    let required = false;
                    for (let k = 0; k < this.features.length; k += 1) {
                        const tmpDependencies = this.features[k].getDependencies();
                        if (this.features[k].getSettingValue('ENABLED') === '0') {
                            continue;
                        }
                        if (tmpDependencies.includes(dependency) && k !== i) {
                            required = true;
                            break;
                        }
                    }
                    if (!required) {
                        for (let k = 0; k < this.features.length; k += 1) {
                            if (this.features[k].id === dependency) {
                                if (this.features[k].getSettingValue('ENABLED') !== undefined) {
                                    continue;
                                }
                                messages.push({
                                    featureId: this.features[k].getID(),
                                    settingId: undefined,
                                    message: `${this.features[k].name} is no longer required`,
                                    messageType: ValidationMessageType.REMOVE,
                                });
                                break;
                            }
                        }
                    }
                }
            }
            if (feature.id.indexOf('node_') === 0) {
                if (feature.id === 'node_link') {
                    const sourcePort = feature.getSettingValue('SOURCE_PORT');
                    const source: BaseNode = this.getFeature(feature.getSettingValue('SOURCE_FEATURE')) as BaseNode;
                    const targetPort = feature.getSettingValue('TARGET_PORT');
                    const target: BaseNode = this.getFeature(feature.getSettingValue('TARGET_FEATURE')) as BaseNode;
                    if (source === undefined
                        || target === undefined
                        || source.getPort(sourcePort) === undefined
                        || target.getPort(targetPort) === undefined
                    ) {
                        messages.push({
                            featureId: this.features[i].getID(),
                            settingId: undefined,
                            message: `${this.features[i].name} is no longer required`,
                            messageType: ValidationMessageType.REMOVE,
                        });
                        continue;
                    }
                } else if ((feature.getSettingValue('FEATURE') !== '' && feature.getSettingValue('FEATURE') !== undefined)
                    && this.getFeature(feature.getSettingValue('FEATURE')) === undefined) {
                    messages.push({
                        featureId: this.features[i].getID(),
                        settingId: undefined,
                        message: `${this.features[i].name} is no longer required`,
                        messageType: ValidationMessageType.REMOVE,
                    });
                }
            }
            if (feature.id === 'linked_setting') {
                const LINKEDSETTING = feature.getSettingValue('LINKEDSETTING');
                const linkedSettingFeature = this.getFeature(LINKEDSETTING);
                const FEATUREID = feature.getSettingValue('FEATURE');
                const linkedFeature = this.getFeature(FEATUREID);
                if (!linkedFeature || !linkedSettingFeature) {
                    messages.push({
                        featureId: this.features[i].getID(),
                        settingId: undefined,
                        message: `${this.features[i].name} is no longer required`,
                        messageType: ValidationMessageType.REMOVE,
                    });
                    continue;
                }
            }
            if (feature.id === 'global_vars') {
                const vars = feature.getSettingValue('AUTOVARS');
                for (let j = 0; j < vars.length; j += 1) {
                    const varItem = vars[j];
                    const linkedFeatureId = varItem.getSettingValue('FEATURE');
                    if (linkedFeatureId) {
                        const linkedFeature = this.getFeature(linkedFeatureId);
                        if (linkedFeature === undefined) {
                            messages.push({
                                featureId: varItem.getID(),
                                settingId: undefined,
                                message: `${varItem.name} is no longer required`,
                                messageType: ValidationMessageType.REMOVE,
                            });
                        }
                    }
                }
            }
        }
        this.setProject(this.features, this);
        return messages;
    }

    getSettingsWithName(feature: BaseFeature, results: { [key: string]: any[] }) {
        for (let i = 0; i < feature.settings.length; i += 1) {
            const setting = feature.settings[i];
            if (setting.settingType === SettingType.NAME) {
                let settingValue = setting.value;
                if (this.runtime === RUNTIMES.TIOS) {
                    settingValue = settingValue.toLowerCase();
                }
                if (results[settingValue] === undefined) {
                    results[settingValue] = [];
                }
                results[settingValue].push({
                    featureId: feature.getID(),
                    settingId: setting.id,
                });
            } else if (setting.settingType === SettingType.ARRAY) {
                if (setting.value !== undefined) {
                    for (let j = 0; j < setting.value.length; j += 1) {
                        this.getSettingsWithName(setting.value[j], results);
                    }
                }
            }
        }
    }

    getRequiredSettings(): Array<string> {
        const result: Array<string> = [];
        for (let i = 0; i < this.features.length; i += 1) {
            const feature: BaseFeature = this.features[i];
            if (feature.getSettingValue('ENABLED') === '1') {
                const linkedSettings = [];
            }
        }
        return result;
    }

    getFeature(
        id: string, features: BaseFeature[] | undefined = undefined,
    ): BaseFeature | undefined {
        let currentFeatures = features;
        if (currentFeatures === undefined) {
            currentFeatures = this.features;
        }
        for (let i = 0; i < currentFeatures.length; i += 1) {
            const feature = currentFeatures[i];
            if (feature.id === id || (id !== undefined && feature.getID() === id)) {
                return feature;
            }
            for (let j = 0; j < feature.settings.length; j += 1) {
                if (feature.settings[j].settingType === SettingType.ARRAY) {
                    if (feature.settings[j].value !== undefined) {
                        const tmp = this.getFeature(id, feature.settings[j].value);
                        if (tmp !== undefined) {
                            return tmp;
                        }
                    }
                }
            }
        }
        return undefined;
    }

    getParent(feature: BaseFeature, parent?: BaseFeature): any {
        const cached = this.parentCache[feature.getID()];
        if (cached !== undefined) {
            return cached;
        }
        if (parent === undefined) {
            for (let i = 0; i < this.features.length; i += 1) {
                const tmpFeature = this.features[i];
                if (tmpFeature.getID() === feature.getID()) {
                    return undefined;
                }
                const tmp = this.getParent(feature, this.features[i]);
                if (tmp !== undefined) {
                    return tmp;
                }
            }
        } else {
            for (let i = 0; i < parent.settings.length; i += 1) {
                if (parent.settings[i].settingType === SettingType.ARRAY && parent.settings[i].value !== undefined) {
                    const ids = parent.settings[i].value.map((f: BaseFeature) => f.getID());
                    if (ids.includes(feature.getID())) {
                        this.parentCache[feature.getID()] = parent;
                        return parent;
                    }
                    for (let j = 0; j < parent.settings[i].value.length; j += 1) {
                        this.parentCache[parent.settings[i].value[j].getID()] = parent;
                        const tmp = this.getParent(feature, parent.settings[i].value[j]);
                        if (tmp !== undefined) {
                            return tmp;
                        }
                    }
                }
            }
        }
        return undefined;
    }

    getParentFeature(
        id: string, features: BaseFeature[] | undefined = undefined,
    ): BaseFeature | undefined {
        let currentFeatures = features;
        if (currentFeatures === undefined) {
            currentFeatures = this.features;
        }
        for (let i = 0; i < currentFeatures.length; i += 1) {
            const feature = currentFeatures[i];
            if (feature.id === id) {
                return feature;
            }
            for (let j = 0; j < feature.settings.length; j += 1) {
                if (feature.settings[j].settingType === SettingType.ARRAY) {
                    if (feature.settings[j].refId === id) {
                        return feature;
                    }
                    if ((feature.settings[j].value === undefined
                        || feature.settings[j].value.length === 0)
                        && feature.settings[j].refId !== undefined) {
                        const tmp = FeatureUtils.getCodeItem(
                            feature.settings[j].refId as string, this,
                        );
                        const result = this.getParentFeature(id, [tmp]);
                        if (result) {
                            return feature;
                        }
                    } else {
                        const result = this.getParentFeature(id, feature.settings[j].value);
                        if (result !== undefined) {
                            return result;
                        }
                    }
                }
            }
        }
        return undefined;
    }

    getVariables(): Array<BaseFeature> {
        const globalVars: BaseFeature | undefined = this.getFeature('global_vars');
        if (!globalVars) {
            return [];
        }
        const variables = globalVars.getSettingValue('VARS').concat(globalVars.getSettingValue('AUTOVARS'));
        return variables;
    }

    getVariableByName(name: string): BaseFeature | undefined {
        const variables = this.getVariables();
        for (let i = 0; i < variables.length; i += 1) {
            const variable = variables[i];
            if (variable.getSettingValue('VARNAME') === name) {
                return variable;
            }
        }
        return undefined;
    }

    getFeatureArray(id: string, features: BaseFeature[] | undefined = undefined): BaseFeature[] {
        let currentFeatures = features;
        if (currentFeatures === undefined) {
            currentFeatures = this.features;
        }
        let result: BaseFeature[] = [];
        for (let i = 0; i < currentFeatures.length; i += 1) {
            const feature = currentFeatures[i];
            if (feature.id === id) {
                result = result.concat(feature);
            }
            for (let j = 0; j < feature.settings.length; j += 1) {
                if (feature.settings[j].settingType === SettingType.ARRAY) {
                    let currentValue = feature.settings[j].value;
                    if (currentValue === undefined) {
                        currentValue = [];
                    }
                    if (feature.settings[j].refId === id) {
                        result = result
                            .concat(currentValue);
                        continue;
                    }
                    if (feature.settings[j].value !== undefined
                        && feature.settings[j].value.length > 0) {
                        result = result.concat(this.getFeatureArray(id, currentValue));
                    }
                }
            }
        }
        return result;
    }

    getDefaultValue(featureId: string, defaultValue: string, defaultValueSetting: string) {
        let index = 1;
        const features = this.getFeatureArray(featureId);
        const regex = new RegExp(`^${defaultValue}[1-9][0-9]*$`);
        for (let i = 0; i < features.length; i++) {
            const featureName = features[i].getSettingValue(defaultValueSetting);
            if (regex.test(featureName)) {
                const num = Number(featureName.replace(defaultValue, ''));
                if (num >= index) {
                    index = num + 1;
                }
            }
        }
        return defaultValue + index;
    }

    getCodeLocation(featureId: string, location: string) {
        const feature = this.getFeature(featureId);
        if (!feature?.singleInstance) {
            return `${feature?.getSettingValue('NAME')}_${location}`;
        }
        return location;
    }

    getCodeLocationStart(): string {
        return PlatformUtils.getRuntimeCodeLocationStart(this.runtime);
    }

    getCodeLocationEnd(): string {
        return PlatformUtils.getRuntimeCodeLocationEnd(this.runtime);
    }

    convertDataType(
        expression: string,
        sourceDataType: TibboDataType,
        expectedDataType: TibboDataType,
        timeFormat?: string,
    ): string {
        const generalFeature = this.getFeature('general');
        if (timeFormat === undefined) {
            timeFormat = generalFeature?.getSettingValue('TIME_FORMAT');
        }
        return PlatformUtils.convertDataType(
            expression,
            sourceDataType,
            expectedDataType,
            this.runtime,
            timeFormat,
        );
    }

    getOutputChain(node: BaseNode, chain: BaseNode[] = []): BaseNode[] {
        const outputs: AppBlockPort[] = node.getOutputs();
        for (let i = 0; i < outputs.length; i++) {
            const outputLinks = this.getNodeLinks(node, outputs[i].portId);
            if (outputLinks.length > 0) {
                for (let j = 0; j < outputLinks.length; j++) {
                    const linkedNodeId = outputLinks[j].getSettingValue('TARGET_FEATURE');
                    const linkedNode = this.getFeature(linkedNodeId);
                    chain.push(linkedNode as BaseNode);
                    this.getOutputChain(linkedNode as BaseNode, chain);
                }
            }
        }
        return chain;
    }

    getInputChain(node: BaseNode, chain: BaseNode[] = []): BaseNode[] {
        const inputs: AppBlockPort[] = node.getInputs();
        for (let i = 0; i < inputs.length; i++) {
            const inputLinks = this.getNodeLinks(node, inputs[i].portId);
            if (inputLinks.length > 0) {
                for (let j = 0; j < inputLinks.length; j++) {
                    const linkedNodeId = inputLinks[j].getSettingValue('SOURCE_FEATURE');
                    const linkedNode = this.getFeature(linkedNodeId);
                    chain.push(linkedNode as BaseNode);
                    this.getInputChain(linkedNode as BaseNode, chain);
                }
            }
        }
        return chain;
    }

    getNodeParameterValue(node: BaseNode, paramName: string) {
        const args = node.getSettingValue('ARGUMENTS');
        for (let i = 0; i < args.length; i++) {
            const arg = args[i];
            if (arg.getSettingValue('TARGETPARAM') === paramName) {
                return arg.getSettingValue('ARGVALUE');
            }
        }
        return undefined;
    }

    getNodeParameterExpression(targetNode: BaseFeature, paramName: string) {
        const args = targetNode.getSettingValue('ARGUMENTS');
        for (let i = 0; i < args.length; i += 1) {
            const arg = args[i];
            if (arg.getSettingValue('TARGETPARAM') === paramName) {
                let argExpression = arg.getSettingValue('ARGVALUE');
                const global_vars = this.getFeature('global_vars')?.getSettingValue('VARS');
                const exp = /\{\{\$(\w+)\}\}/g;
                let match = exp.exec(argExpression);
                let found = false;
                while (match != null) {
                    const varName = match[1];
                    for (let j = 0; j < global_vars.length; j += 1) {
                        if (global_vars[j].getID() === varName) {
                            argExpression = argExpression.replace(match[0], global_vars[j].getSettingValue('VARNAME'));
                            found = true;
                        }
                    }
                    if (!found) {
                        argExpression = argExpression.replace(match[0], match[1]);
                    }
                    match = exp.exec(argExpression);
                }
                return argExpression;
            }
        }
        return '';
    }

    getNodeParameter(link: BaseFeature, paramName: string) {
        const targetNode = this.getFeature(link.getSettingValue('TARGET_FEATURE'));
        const args = targetNode?.getSettingValue('ARGUMENTS');
        for (let i = 0; i < args.length; i += 1) {
            const arg = args[i];
            if (arg.getSettingValue('TARGETPARAM') === paramName) {
                let dataType;
                const argExpression = arg.getSettingValue('ARGVALUE');
                const source: BaseNode = this.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
                const sourceParams = source.getPort(link.getSettingValue('SOURCE_PORT'))?.params;
                const target: BaseNode = this.getFeature(link.getSettingValue('TARGET_FEATURE')) as BaseNode;
                const targetParams = target.getPort(link.getSettingValue('TARGET_PORT'))?.params;
                let strExpression = argExpression.toString();
                let targetParam;
                if (targetParams === undefined) {
                    return '';
                }
                for (let j = 0; j < targetParams.length; j++) {
                    if (targetParams[j].name === paramName) {
                        targetParam = targetParams[j];
                        dataType = targetParam.dataType;
                        break;
                    }
                }
                if (targetParam === undefined) {
                    return '';
                }
                // empty expression
                if (argExpression === '') {
                    if (targetParam.dataType === TibboDataType.STRING) {
                        return '""';
                    }
                    return '""';
                }
                // literal expression
                if (strExpression.indexOf('{{') < 0 && strExpression.indexOf('}}') < 0) {
                    strExpression = FeatureUtils.escapeString(strExpression, this.runtime);
                    let parsedExpression = argExpression;
                    if (targetParam.dataType === TibboDataType.STRING) {
                        // escape strings
                        return `"${strExpression}"`;
                    }
                    parsedExpression = this.convertDataType(
                        argExpression, TibboDataType.FLOAT, targetParam.dataType,
                    );

                    return parsedExpression;
                }

                return this.parseCodeExpression(strExpression, sourceParams, dataType);
            }
        }
        return '';
    }

    getNodeLinks(node: BaseNode, portId: string): NodeLink[] {
        const results: NodeLink[] = [];
        const ports = node.getInputs().concat(node.getOutputs());
        const nodeId = node.getID();
        let ID = '';

        for (let i = 0; i < ports.length; i += 1) {
            if (ports[i].portId === portId) {
                ID = ports[i].portId;
            }
        }

        for (let i = 0; i < this.features.length; i += 1) {
            const feature = this.features[i] as NodeLink;
            if (feature.id === 'node_link') {
                if (
                    (nodeId === feature.getSettingValue('SOURCE_FEATURE') && feature.getSettingValue('SOURCE_PORT') === ID)
                    || (nodeId === feature.getSettingValue('TARGET_FEATURE') && feature.getSettingValue('TARGET_PORT') === ID)
                ) {
                    results.push(feature);
                }
            }
        }
        return results;
    }

    getNetworkConnectedLocation(networkInterface: string) {
        return 'services_start';
        // return `NETWORK_CONNECTED_${networkInterface}`;
    }

    getNetworkInterfaces() {
        const interfaces = [];
        if (this.device.platform === TIBBO_DEVICE_ID.WM2000) {
            interfaces.push({
                name: 'Wi-Fi',
                value: 'PL_SOCK_INTERFACE_WLN',
            });
        } else {
            interfaces.push({
                name: 'Ethernet',
                value: 'PL_SOCK_INTERFACE_NET',
            });
            const wifiFeature = this.getFeature('wifi');
            if (wifiFeature && wifiFeature.getSettingValue('ENABLED') === '1') {
                interfaces.push({
                    name: 'Wi-Fi',
                    value: 'PL_SOCK_INTERFACE_WLN',
                });
            }
            const cellFeature = this.getFeature('cell');
            if (cellFeature && cellFeature.getSettingValue('ENABLED') === '1') {
                interfaces.push({
                    name: 'Cellular',
                    value: 'PL_SOCK_INTERFACE_PPP',
                });
            }
        }
        return interfaces;
    }

    getModbusSerialPorts() {
        const options: SettingOption[] = [];
        options.push({
            value: '',
            name: 'Undefined',
        });
        for (let i = 0; i < this.features.length; i++) {
            const feature = this.features[i];
            const serialFeatures = [
                'tibbit_01',
                'ser485',
                'ser232',
                'ser',
            ];

            if (feature.getSettingValue('MODE') === 'RS485'
                || feature.getSettingValue('MODE') === 'RS422'
                || feature.getSettingValue('MODE') === 'RS232'
                || serialFeatures.includes(feature.id)) {
                options.push({
                    value: feature.getSettingValue('ID'),
                    name: feature.getSettingValue('NAME'),
                });
            }
        }
        return options;
    }

    getInterruptLines() {
        const options: SettingOption[] = [];
        options.push({
            value: '',
            name: 'Undefined',
        });
        if (this.device.deviceType === DeviceType.TPP) {
            for (let i = 0; i < this.features.length; i++) {
                const feature = this.features[i];
                if (feature.id.indexOf('tibbit_') === 0) {
                    if (feature.getSettingValue('L4MODE') === IOLinesMode.Interrupt) {
                        // get previous setting
                        const name = feature.getSettingValue('L4NAME');
                        options.push({
                            value: feature.getID(),
                            name,
                        });
                    }
                }
            }
        }

        return options;
    }

    getLinkedSetting(featureId: string, settingId: string) {
        for (let i = 0; i < this.features.length; i++) {
            const feature = this.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === featureId
                    && feature.getSettingValue('SETTINGID') === settingId) {
                    return feature;
                }
            }
        }
        return undefined;
    }
}
